import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { Card, Empty, ErrorNote, Loading, PageHead, Pill, useResource, useToast } from '../components/ui';
import { dateTime } from '../lib/format';

/**
 * Two things, kept apart on purpose.
 *
 * The checklist is what a DPO reads: every check the platform runs, stated as
 * the condition that holds when it passes, with Compliant or N exceptions
 * beside it. The report is what an auditor or the CA firm takes away: the
 * figures for a period, signed, as a PDF. The record-integrity result belongs
 * in the report, not on a tile.
 */

interface Check {
  code: string;
  statement: string;
  basis: string;
  href: string;
  status: 'compliant' | 'exceptions';
  exceptions: number;
  items: Array<{ title: string; href: string; since: string }>;
}

interface Posture {
  tenantName: string;
  from: string;
  to: string;
  generatedAt: string;
  purposes: Array<{ key: string; name: string; basis: string; retentionDays: number | null; status: string }>;
  notices: Array<{ key: string; version: number; languages: string[]; publishedAt: string | null }>;
  collectionPoints: Array<{ name: string; channel: string; status: string; captured: number }>;
  consents: {
    principals: number; live: number; declined: number; withdrawn: number;
    expired: number; capturedInPeriod: number;
  };
  withdrawals: {
    inPeriod: number; medianPropagationSeconds: number | null;
    slowestPropagationSeconds: number | null; undelivered: number;
  };
  integrity: {
    status: string; eventsChecked: number; verificationsInPeriod: number; lastVerifiedAt: string | null;
  };
  contentHash: string;
  signature: { keyId: string };
}

const CHANNEL: Record<string, string> = {
  web: 'Website', app: 'Mobile app', api: 'System integration', offline: 'Paper or in person',
  callcentre: 'Call centre', cookie: 'Cookie banner',
  paper: 'Paper form', kiosk: 'Kiosk', ivr: 'Phone (IVR)', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp',
};

function duration(seconds: number | null): string {
  if (seconds === null) return '';
  if (seconds < 90) return `${seconds} seconds`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} minutes`;
  return `${(seconds / 3600).toFixed(1)} hours`;
}

type PeriodKey = 'quarter' | 'previous_quarter' | 'year_to_date' | 'last_12_months';

/** Period boundaries in UTC, as the API expects. */
function periodRange(key: PeriodKey): { from: string; to: string; label: string } {
  const now = new Date();
  const y = now.getUTCFullYear();
  const q = Math.floor(now.getUTCMonth() / 3);
  const iso = (d: Date) => d.toISOString();
  const qName = (year: number, quarter: number) => `Q${quarter + 1} ${year}`;
  switch (key) {
    case 'previous_quarter': {
      const from = new Date(Date.UTC(y, q * 3 - 3, 1));
      const to = new Date(Date.UTC(y, q * 3, 1));
      return { from: iso(from), to: iso(to), label: qName(from.getUTCFullYear(), Math.floor(from.getUTCMonth() / 3)) };
    }
    case 'year_to_date':
      return { from: iso(new Date(Date.UTC(y, 0, 1))), to: iso(now), label: `${y} to date` };
    case 'last_12_months':
      return { from: iso(new Date(Date.UTC(y - 1, now.getUTCMonth(), 1))), to: iso(now), label: 'Last 12 months' };
    default:
      return { from: iso(new Date(Date.UTC(y, q * 3, 1))), to: iso(now), label: `${qName(y, q)} to date` };
  }
}

export default function PosturePage() {
  const toast = useToast();
  const [reload, setReload] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [period, setPeriod] = useState<PeriodKey>('quarter');
  const range = useMemo(() => periodRange(period), [period]);

  const checks = useResource<{ checks: Check[] }>(() => api.get('/v1/compliance/checks'), [reload]);
  const posture = useResource<Posture>(
    () => api.get(`/v1/reports/posture?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`),
    [reload, range.from, range.to],
  );

  const list = checks.data?.checks ?? [];
  const failing = list.filter((c) => c.status === 'exceptions');

  async function recheck() {
    setBusy(true);
    try {
      await api.post('/v1/signals/refresh');
      setReload((n) => n + 1);
      toast('Checks run.', 'ok');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function download() {
    // Through fetch, so the session token travels with the request. A plain
    // link opens without it and the API answers "unauthorized".
    setDownloading(true);
    try {
      await api.download(
        `/v1/reports/posture.pdf?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
        `compliance-report-${range.from.slice(0, 10)}-to-${range.to.slice(0, 10)}.pdf`,
      );
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <PageHead title="Compliance status">
        {can(getUser(), 'posture:run') && <button onClick={recheck} disabled={busy}>{busy ? 'Running…' : 'Run checks'}</button>}
      </PageHead>

      <Card title={list.length ? (failing.length ? `${failing.length} of ${list.length} checks with exceptions` : `${list.length} checks, all compliant`) : 'Checks'}>
        {checks.loading && !checks.data && <Loading what="Running checks" />}
        <ErrorNote error={checks.error} />
        {checks.data && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Check</th>
                  <th>Basis</th>
                  <th>Result</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.code}>
                    <td>
                      {c.statement}
                      {c.items.map((i) => (
                        <div key={i.title} className="small muted" style={{ marginTop: 4 }}>
                          {i.title} · since {dateTime(i.since)}
                        </div>
                      ))}
                    </td>
                    <td className="small muted" style={{ whiteSpace: 'nowrap' }}>{c.basis}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {c.status === 'compliant'
                        ? <Pill tone="ok">Compliant</Pill>
                        : <Pill tone="bad">{c.exceptions} exception{c.exceptions === 1 ? '' : 's'}</Pill>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {c.status === 'exceptions' && <Link className="btn small" to={c.items[0]?.href ?? c.href}>Open</Link>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <h2 className="section" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span>Report · {range.label}</span>
        <select value={period} onChange={(e) => setPeriod(e.target.value as PeriodKey)} style={{ width: 200, fontWeight: 400, fontSize: 14 }}>
          <option value="quarter">Current quarter to date</option>
          <option value="previous_quarter">Previous quarter</option>
          <option value="year_to_date">Year to date</option>
          <option value="last_12_months">Last 12 months</option>
        </select>
        <button className="primary" onClick={download} disabled={downloading || !posture.data} style={{ marginInlineStart: 'auto' }}>
          {downloading ? 'Preparing…' : 'Download signed PDF'}
        </button>
      </h2>

      {posture.loading && !posture.data && <Loading what="Building the report" />}
      <ErrorNote error={posture.error} />

      {posture.data && (
        <>
          <div className="stat-row">
            <Card><div className="stat">
              <h3>People on record</h3>
              <div className="value">{posture.data.consents.principals}</div>
              <div className="sub">{posture.data.consents.capturedInPeriod} decisions in this period</div>
            </div></Card>
            <Card><div className="stat">
              <h3>Live consents</h3>
              <div className="value">{posture.data.consents.live}</div>
              <div className="sub">{posture.data.consents.declined} declined · {posture.data.consents.withdrawn} withdrawn · {posture.data.consents.expired} expired</div>
            </div></Card>
            <Card><div className="stat">
              <h3>Withdrawals in period</h3>
              <div className={`value ${posture.data.withdrawals.undelivered ? 'bad' : ''}`}>{posture.data.withdrawals.inPeriod}</div>
              <div className="sub">
                {posture.data.withdrawals.undelivered > 0
                  ? `${posture.data.withdrawals.undelivered} not yet relayed to processors`
                  : posture.data.withdrawals.inPeriod === 0
                    ? 'none in this period'
                    : `processors told within ${duration(posture.data.withdrawals.medianPropagationSeconds)} (median)`}
              </div>
            </div></Card>
            <Card><div className="stat">
              <h3>Notices in force</h3>
              <div className="value">{posture.data.notices.length}</div>
              <div className="sub">{posture.data.collectionPoints.filter((c) => c.status === 'active').length} active collection points</div>
            </div></Card>
          </div>

          <div className="grid cols-2">
            <Card title="Purposes">
              <table className="data">
                <tbody>
                  {posture.data.purposes.map((p) => (
                    <tr key={p.key}>
                      <td><strong>{p.name}</strong></td>
                      <td className="small">
                        {p.basis === 'consent' ? 'Consent (S.6)' : 'Legitimate use (S.7)'}
                        <div className="muted">
                          {p.retentionDays ? `Kept ${p.retentionDays} days` : 'Kept until withdrawn'}
                        </div>
                      </td>
                      <td>{p.status === 'active' ? <Pill tone="ok">Active</Pill> : <Pill>{p.status}</Pill>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <div>
              <Card title="Notices">
                {!posture.data.notices.length && <div className="note bad">No published notice.</div>}
                <table className="data">
                  <thead><tr><th>Notice</th><th>Languages</th><th>Published</th></tr></thead>
                  <tbody>
                    {posture.data.notices.map((n) => (
                      <tr key={n.key}>
                        <td><strong>{n.key.replace(/_/g, ' ')}</strong> <span className="muted">v{n.version}</span></td>
                        <td className="small">{n.languages.join(', ')}</td>
                        <td className="small muted">{n.publishedAt ? n.publishedAt.slice(0, 10) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>

              <Card title="Collection points">
                <table className="data">
                  <thead><tr><th>Touch point</th><th>Channel</th><th className="num">Decisions in period</th><th /></tr></thead>
                  <tbody>
                    {posture.data.collectionPoints.map((cp) => (
                      <tr key={cp.name}>
                        <td><strong>{cp.name}</strong></td>
                        <td className="small">{CHANNEL[cp.channel] ?? cp.channel}</td>
                        <td className="num">{cp.captured}</td>
                        <td>{cp.status === 'active' ? <Pill tone="ok">Active</Pill> : <Pill>{cp.status}</Pill>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          </div>

          <Card title="About this report">
            <div className="small muted">
              Covers {posture.data.from.slice(0, 10)} to {posture.data.to.slice(0, 10)}, generated {dateTime(posture.data.generatedAt)}.
              Consent records {posture.data.integrity.status === 'intact' ? 'verified intact' : 'FAILED verification'}
              {' '}({posture.data.integrity.eventsChecked} events{posture.data.integrity.lastVerifiedAt ? `, last checked ${dateTime(posture.data.integrity.lastVerifiedAt)}` : ''}).
              The PDF carries the same figures, signed with key <code className="mono">{posture.data.signature.keyId}</code>.
            </div>
          </Card>
        </>
      )}
    </>
  );
}
